import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const wholesaleD4DApi = createApi({
  reducerPath: "wholesaleD4DApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["WholesaleD4D", "WholesaleD4DStats"],
  endpoints: (build) => ({
    // Get all D4D properties
    getAllD4DProperties: build.query({
      query: ({ condition, status, neighborhood, search, page = 1, limit = 100, starred } = {}) => ({
        url: "/wholesale/d4d",
        method: "GET",
        params: { condition, status, neighborhood, search, page, limit, starred },
      }),
      providesTags: ["WholesaleD4D"],
    }),

    // Get D4D property by ID
    getD4DPropertyById: build.query({
      query: (id) => ({
        url: `/wholesale/d4d/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "WholesaleD4D", id }],
    }),

    // Create D4D property
    createD4DProperty: build.mutation({
      query: (property) => ({
        url: "/wholesale/d4d",
        method: "POST",
        body: property,
      }),
      invalidatesTags: ["WholesaleD4D", "WholesaleD4DStats"],
    }),

    // Bulk create D4D properties
    bulkCreateD4DProperties: build.mutation({
      query: (payload) => ({
        url: "/wholesale/d4d/bulk",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["WholesaleD4D", "WholesaleD4DStats"],
    }),

    // Update D4D property
    updateD4DProperty: build.mutation({
      query: ({ id, ...property }) => ({
        url: `/wholesale/d4d/${id}`,
        method: "PUT",
        body: property,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleD4D", id },
        "WholesaleD4D",
        "WholesaleD4DStats",
      ],
    }),

    // Delete D4D property
    deleteD4DProperty: build.mutation({
      query: (id) => ({
        url: `/wholesale/d4d/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WholesaleD4D", "WholesaleD4DStats"],
    }),

    // Update D4D status
    updateD4DStatus: build.mutation({
      query: ({ id, status }) => ({
        url: `/wholesale/d4d/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleD4D", id },
        "WholesaleD4D",
        "WholesaleD4DStats",
      ],
    }),

    // Toggle star
    toggleD4DStar: build.mutation({
      query: (id) => ({
        url: `/wholesale/d4d/${id}/star`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "WholesaleD4D", id },
        "WholesaleD4D",
      ],
    }),

    // Add skip trace info
    addSkipTraceInfo: build.mutation({
      query: ({ id, ownerName, ownerPhone, ownerEmail, ownerAddress }) => ({
        url: `/wholesale/d4d/${id}/skip-trace`,
        method: "PATCH",
        body: { ownerName, ownerPhone, ownerEmail, ownerAddress },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleD4D", id },
        "WholesaleD4D",
        "WholesaleD4DStats",
      ],
    }),

    // Convert to seller
    convertD4DToSeller: build.mutation({
      query: (id) => ({
        url: `/wholesale/d4d/${id}/convert-to-seller`,
        method: "POST",
      }),
      invalidatesTags: ["WholesaleD4D", "WholesaleD4DStats", "WholesaleSeller"],
    }),

    // Get D4D stats
    getD4DStats: build.query({
      query: () => ({
        url: "/wholesale/d4d/stats",
        method: "GET",
      }),
      providesTags: ["WholesaleD4DStats"],
    }),

    // Get properties by route
    getPropertiesByRoute: build.query({
      query: (route) => ({
        url: `/wholesale/d4d/route/${encodeURIComponent(route)}`,
        method: "GET",
      }),
      providesTags: ["WholesaleD4D"],
    }),

    // Archive a batch (Done With List)
    archiveBatch: build.mutation({
      query: (batchTitle) => ({
        url: "/wholesale/d4d/archive-batch",
        method: "POST",
        body: { batchTitle },
      }),
      invalidatesTags: ["WholesaleD4D", "WholesaleD4DStats"],
    }),

    // Unarchive a batch (Restore from History)
    unarchiveBatch: build.mutation({
      query: (batchTitle) => ({
        url: "/wholesale/d4d/unarchive-batch",
        method: "POST",
        body: { batchTitle },
      }),
      invalidatesTags: ["WholesaleD4D", "WholesaleD4DStats"],
    }),
  }),
});

export const {
  useGetAllD4DPropertiesQuery,
  useGetD4DPropertyByIdQuery,
  useCreateD4DPropertyMutation,
  useBulkCreateD4DPropertiesMutation,
  useUpdateD4DPropertyMutation,
  useDeleteD4DPropertyMutation,
  useUpdateD4DStatusMutation,
  useToggleD4DStarMutation,
  useAddSkipTraceInfoMutation,
  useConvertD4DToSellerMutation,
  useGetD4DStatsQuery,
  useGetPropertiesByRouteQuery,
  useArchiveBatchMutation,
  useUnarchiveBatchMutation,
} = wholesaleD4DApi;
